import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgScrollbar } from 'ngx-scrollbar';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { CandidatesService } from 'src/app/services/api/candidates.service';
import { EmployeesService } from 'src/app/services/api/employees.service';
import { JobPostingsService } from 'src/app/services/api/job-postings.service';
import { AuthService } from 'src/app/services/auth.service';
import { ContextService } from 'src/app/services/context.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { successData, tapFinished, tapLoading } from 'src/app/utils/rxjs-operators';
import { ProgressService } from 'src/app/services/progress.service';
import { MessagesStatuses } from 'src/app/enum';
import { CampaignService } from 'src/app/services/api/campaign.service';
import { XrefService } from 'src/app/services/api/xref.service';

@Component({
  selector: 'app-chat-view-component',
  templateUrl: './chat-view.component.html',
  styleUrls: ['./chat-view.component.scss']
})
export class ChatViewComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private intervalRefresh: any;

  public smsMsg = '';
  public chat: any[] = [];
  public activeTopic: any[] = [];
  public recentTopics: any[] = [];
  public recommendedJobs: any[] = [];
  public usersPhoneNumbers: any[] = [];
  public searchedCandidates: any[] = [];
  public selectedUser: any;
  public errorMessage: any;
  public modalRef: any;
  public localProfiles: any[] = [];
  public isBulkSent = false;
  public bulkAllErrors = true;
  public bulkSuccessMsg = '';
  public smsIsLoading = false;
  public MessagesStatuses = MessagesStatuses;


  @Input() isBulk = false;
  @Input()
  set profiles(value: any[]) {
    this.localProfiles = [...(value ?? [])];
  }
  @Input() jobId: any = null;
  @Input() jobInfo: any;
  @Output() triggerParentAction = new EventEmitter<string>();
  @HostListener('window:resize')
  @ViewChild('scrollbarMsg') scrollbarRef!: NgScrollbar;
  @ViewChild('msgInput') msgInput?: NgScrollbar;
  @ViewChild('bulkMessages') bulkMessages!: NgScrollbar;
  @ViewChild('emojiTrigger') emojiTrigger!: MatMenuTrigger;
  @ViewChild('smsTemplateTrigger') smsTemplateTrigger!: MatMenuTrigger;
  @ViewChild('smartTextTemplateTrigger') smartTextTemplateTrigger!: MatMenuTrigger;
  @ViewChild('hiddenCandidateSearchInput') hiddenCandidateSearchInput!: ElementRef;
  @ViewChild('archiveModal', { static: false }) archiveModal!: TemplateRef<any>;
  @ViewChild('bulkCandidatesSearchMenu', { static: false }) bulkCandidatesSearchMenu!: ElementRef;
  @ViewChild('activeLineMenuTrigger', { read: MatMenuTrigger }) activeLineMenuTrigger!: MatMenuTrigger;

  outreachVars$ = this.xrefService.getSMSOutreachVars().pipe(successData(), takeUntil(this.destroy$));

  outreachTempalte$ = this.campaignService.getSMSTemplates().pipe(successData(), takeUntil(this.destroy$));
  constructor(
    private renderer: Renderer2,
    private candidatesService: CandidatesService,
    private refreshSvc: RefreshService,
    private progress: ProgressService,
    private employeesService: EmployeesService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    public context: ContextService,
    public auth: AuthService,
    public jobPostingsService: JobPostingsService,
    public xrefService: XrefService,
    public campaignService: CampaignService
  ) { }

  public ngOnInit() {
    if (this.localProfiles.length > 1) {
      this.isBulk = true;
    }

    this.selectedUser = { ...this.auth.userInfo };

    if (!this.isBulk) {
      this.fetchChat();

      // TODO refactor to RXJS intervals
      this.intervalRefresh = setInterval(() => {
        this.fetchChat();
      }, 10000);

      this.candidatesService
        .chatTopics(this.localProfiles[0].profileRef)
        .pipe(successData(), takeUntil(this.destroy$))
        .subscribe((data: any) => {
          this.activeTopic = data.filter((topic: any) => topic.status === 1);
          if (this.activeTopic?.length === 0) {
            if (this.context.context[0]) this.activeTopic.push({ jobTitle: this.context.latest.jobTitle, jobId: this.context.latest.jobId });
          }
        
          if (!this.isBulk) {
            this.recentTopics = data.filter((topic: any) => topic.status === 0);
            this.candidatesService
              .recommendedJobs(this.localProfiles[0].profileRef)
              .pipe(successData(), takeUntil(this.destroy$))
              .subscribe((data: any) => {
                this.recommendedJobs = data.filter(
                  (job: any) => !this.activeTopic.some(j => j.jobId === job.jobId)
                );
              });
          }
          if (this.activeTopic?.length > 0) this.checksendSmsAbility();
        });
    } else {
      this.checksendSmsAbility();
    }

    this.employeesService
      .getRecruiterPhoneNumbers()
      .pipe(successData(), takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.usersPhoneNumbers = data;
      });
  }

  public ngAfterViewInit(): void {
    this.adjustScrollbarHeight();

    setTimeout(() => {
      this.scrollChatViewToBottom();
    }, 300);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.intervalRefresh) {
      clearInterval(this.intervalRefresh);
    }
  }

  private fetchChat() {
    this.candidatesService
      .getCandidateChat(this.localProfiles[0].profileRef, this.selectedUser.userId)
      .pipe(
        successData(),
        takeUntil(this.destroy$))
      .subscribe(data => {
        const transformedData = data.map((message: any) => ({
          ...message,
          date: this.getChatDateLabel(message.timeStamp)
        }));

        if (JSON.stringify(transformedData) !== JSON.stringify(this.chat)) {
          this.chat = transformedData;
          this.refreshSvc.setRefreshSmsObs(true);

          setTimeout(() => {
            this.scrollChatViewToBottom();
          }, 100);
        } else {
          this.chat = transformedData;
        }
      });
  }

  private getChatDateLabel(value: string): string {
    const date = new Date(value);
    const now = new Date();
    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (isSameDay(date, now)) return 'Today';
    if (isSameDay(date, yesterday)) return 'Yesterday';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    return date.toLocaleDateString(undefined, options);
  }

  private onResize() {
    this.adjustScrollbarHeight();
  }

  private adjustScrollbarHeight() {
    if (this.scrollbarRef) {
      const parent = this.scrollbarRef.nativeElement.parentElement;

      if (parent) {
        const parentHeight = parent.clientHeight;
        this.renderer.setStyle(this.scrollbarRef.nativeElement, 'height', `${parentHeight}px`);
      }
    }
  }

  private scrollChatViewToBottom() {
    if (this.scrollbarRef) {
      console.log('Scrolling...');
      void this.scrollbarRef.scrollTo({ bottom: 0, duration: 0 });
    } else {
      console.log('ScrollbarRef is undefined');
    }
  }

  public closeChat() {
    this.triggerParentAction.emit(
      this.isBulk
        ? 'bulk'
        : this.localProfiles[0].profileRef);
  }

  public addText(templateTypeId: number) {
    
    this.campaignService
      .getsmsTemplate( this.context.latest.jobId, String(templateTypeId))
      .pipe(successData(), takeUntil(this.destroy$))
      .subscribe(value => {
           
        this.smsMsg = value?.outreachBody;
        this.msgInput!.nativeElement.focus();
      });
  
  }

  public addEmoji(event: any) {
    this.smsMsg += event.emoji.native;
    this.emojiTrigger.closeMenu();
    this.msgInput!.nativeElement.focus();
  }

  public resizeTextArea(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  public openSmsTemplatesMenu() {
    this.smsTemplateTrigger.openMenu();
  }

  public openSmartTextMenu() {
    this.smartTextTemplateTrigger.openMenu();
  }

  public addVar(value: string) {
    this.smsMsg += value;
    this.smartTextTemplateTrigger.closeMenu();
    this.msgInput!.nativeElement.focus();
  }

  public focusOnCandidatesInput() {
    this.hiddenCandidateSearchInput!.nativeElement.focus();

    if (this.bulkMessages) {
      void this.bulkMessages.scrollTo({
        bottom: 0,
        duration: 0
      });
    }
  }

  public searchBulkCandidates(event: any) {
    const input = event.target as HTMLInputElement;
    const searchValue = input.value;

    if (!searchValue) {
      this.bulkCandidatesSearchMenu?.nativeElement.classList.remove('active');
      this.searchedCandidates = [];
      return;
    } else {
      this.bulkCandidatesSearchMenu?.nativeElement.classList.add('active');
    }

    const jobId = this.activeTopic?.length && this.activeTopic[0]?.jobId ? this.activeTopic[0].jobId : this.jobId;

    this.jobPostingsService
      .searchCandidates(searchValue, jobId)
      .pipe(debounceTime(300), successData(), takeUntil(this.destroy$))
      .subscribe((data: any) => {
        this.searchedCandidates = [...data];
      });
  }

  public sendSms() {
    const candidates = this.isBulk ? this.localProfiles.map((profile: any) => profile.profileRef) : [this.localProfiles[0].profileRef];
    const jobIdToUse = this.isBulk ? this.jobId : this.activeTopic?.length && this.activeTopic[0]?.jobId ? this.activeTopic[0].jobId : this.jobId;

    this.candidatesService
      .sendSms(candidates, this.smsMsg, jobIdToUse, this.selectedUser.userId)
      .pipe(
        tapLoading(() => {
          this.smsIsLoading = true;
          this.progress.start();
        }),
        tapFinished(() => {
          this.smsIsLoading = false;
          this.progress.stop();
        }),
        successData(),
        takeUntil(this.destroy$)
      ).subscribe((response: any) => {
        this.errorMessage = response[0]?.errorMessage;

        this.smsMsg = '';

        if (this.isBulk) {
          this.updateProfilesWithErrors(response);

          const successArr: any[] = [];
          const errorArr: any[] = [];

          response.forEach((sms: any) => {
            if (Object.prototype.hasOwnProperty.call(sms, 'errorCode')) {
              errorArr.push(sms);
            } else {
              successArr.push(sms);
            }
          });

          this.isBulkSent = true;
          this.bulkSuccessMsg = successArr.length ? `Successfully delivered ${successArr.length} out of ${response.length} messages!` : 'Delivery failed for all messages. Please try again.';
        } else {
          this.candidatesService
            .getCandidateChat(this.localProfiles[0].profileRef, this.selectedUser.userId)
            .pipe(successData(), takeUntil(this.destroy$))
            .subscribe(data => {
              const transformedData = data.map((message: any) => ({
                ...message,
                date: this.getChatDateLabel(message.timeStamp)
              }));

              this.chat = transformedData;
              this.checksendSmsAbility();
              setTimeout(() => {
                this.scrollChatViewToBottom();
              }, 300);
              this.refreshSvc.setRefreshSmsObs(true);
            });
        }

      });
  }

  updateProfilesWithErrors(response: { profileRef: string; errorCode?: string; errorMessage?: string }[]) {
    console.log('updateProfilesWithErrors -> response:', response);
    const errorMap = new Map(
      response
        .filter(item => !!item.errorCode || !!item.errorMessage)
        .map(item => [item.profileRef, {
          errorCode: item.errorCode,
          errorMessage: item.errorMessage
        }])
    );

    this.bulkAllErrors = true;
    this.localProfiles = this.localProfiles.map(profile => {
      const error = errorMap.get(profile.profileRef);
      if (!error?.errorMessage) {
        this.bulkAllErrors = false;
      }
      return {
        ...profile,
        errorCode: error?.errorCode || null,
        errorMessage: error?.errorMessage || null
      };
    });
  }


  public handleArchiveChat() {
    // SHOW MODAL FIRST
    if (this.archiveModal) this.modalRef = this.dialog.open(this.archiveModal, { minWidth: '500px' });
  }

  public closeConfirmationModal() {
    this.modalRef?.close();
  }

  public archiveChat(profileRef: string, displayName: string) {
    this.candidatesService
      .archiveChat(profileRef)
      .pipe(
        successData(),
        takeUntil(this.destroy$))
      .subscribe(() => {
        this.snackBar.open(`The chat with ${displayName} has been archived`, 'Close', { duration: 5 * 1000 });
        this.closeConfirmationModal();
        this.closeChat();
        setTimeout(() => {
          this.refreshSvc.setRefreshSmsObs(true);
        }, 300);
      });
  }

  public removeActiveTopic() {
    const isExistInRecent = this.recentTopics.find((topic: any) => topic.jobId === this.activeTopic[0].jobId && topic.jobTitle === this.activeTopic[0].jobTitle);

    if (isExistInRecent === undefined) {
      this.recentTopics.unshift(this.activeTopic[0]);
    }

    this.activeTopic = [];
  }

  public addActiveTopic(index: number, type: string) {
    if (type === 'recent') {
      const oldActive = [...this.activeTopic];
      const newActive = this.recentTopics[index];

      this.activeTopic = [newActive];
      this.recentTopics.splice(index, 1);
      this.recentTopics = [...oldActive, ...this.recentTopics];
    } else {
      const oldActive = [...this.activeTopic];
      const newActive = this.recommendedJobs[index];

      this.activeTopic = [newActive];
      this.recommendedJobs.splice(index, 1);
      this.recentTopics = [...oldActive, ...this.recentTopics];
    }
    
    this.checksendSmsAbility();
  }

  public addCandidate(candidate: any) {
    const isExist = this.localProfiles.find((profile: any) => profile.profileRef === candidate.profileRef);
    if (isExist) {
      this.snackBar.open(`${candidate.displayName} has been added before`, 'Close', { duration: 5 * 1000 });
      return;
    }

    this.localProfiles.push(candidate);
    this.searchedCandidates = [];
    this.bulkCandidatesSearchMenu?.nativeElement.classList.remove('active');
    this.hiddenCandidateSearchInput.nativeElement.value = '';
  }

  public selectUser(user: any) {
    this.selectedUser = user;
    this.checksendSmsAbility();
    this.fetchChat();
    this.activeLineMenuTrigger.closeMenu();
  }

  public checksendSmsAbility() {
    const candidates = this.isBulk ? this.localProfiles.map((profile: any) => profile.profileRef) : [this.localProfiles[0].profileRef];
    const jobIdToUse = this.activeTopic?.length && this.activeTopic[0]?.jobId ? this.activeTopic[0].jobId : this.jobId;

    if (jobIdToUse) {
      this.candidatesService
        .checksendSms(
          candidates,
          jobIdToUse,
          this.selectedUser.userId
        )
        .pipe(successData(), takeUntil(this.destroy$))
        .subscribe((response: any) => {
          console.log('checksendSmsAbility -> response: ', response);
          this.errorMessage = response[0]?.errorMessage;
          console.log('checksendSmsAbility -> this.errorMessage: ', this.errorMessage);
          this.updateProfilesWithErrors(response);
        });
    }
  }

  public removeCandidate(profileRef: string) {
    if (this.localProfiles.length <= 1) {
      this.snackBar.open('Cant remove last candidate', 'Close', { duration: 5 * 1000 });
      return;
    }

    const index = this.localProfiles.findIndex((profile: any) => profile.profileRef === profileRef);
    this.localProfiles.splice(index, 1);
  }
}
